/**
 * Environment Configuration
 * Event Master v1.5 - Enterprise Backend Integration
 * ✅ ENTERPRISE CRITICAL: Production-ready configuration
 */

// ======= ENTERPRISE PRODUCTION SETTINGS =======

export const config = {
  // Backend Integration
  API_BASE_URL:
    process.env["REACT_APP_API_BASE_URL"] || "http://localhost:3001/api",
  BACKEND_URL: process.env["REACT_APP_BACKEND_URL"] || "http://localhost:3001",
  WS_URL: process.env["REACT_APP_WS_URL"] || "ws://localhost:3001",

  // ✅ ENTERPRISE CRITICAL: Mock data DISABLED for production
  USE_MOCK_DATA:
    process.env["REACT_APP_USE_MOCK_DATA"] === "true" ? true : false,
  ENTERPRISE_MODE:
    process.env["REACT_APP_ENTERPRISE_MODE"] === "true" ? true : false,
  DEBUG_MODE: process.env["REACT_APP_DEBUG_MODE"] === "true" ? true : false,

  // API Configuration
  API_TIMEOUT: parseInt(process.env["REACT_APP_API_TIMEOUT"] || "30000"),
  API_RETRY_ATTEMPTS: parseInt(
    process.env["REACT_APP_API_RETRY_ATTEMPTS"] || "3",
  ),
  API_RETRY_DELAY: parseInt(process.env["REACT_APP_API_RETRY_DELAY"] || "1000"),

  // Security
  CSRF_PROTECTION:
    process.env["REACT_APP_CSRF_PROTECTION"] === "false" ? false : true,
  SECURITY_HEADERS:
    process.env["REACT_APP_SECURITY_HEADERS"] === "false" ? false : true,

  // Features
  FEATURES: {
    AI_SUPPORT: process.env["REACT_APP_AI_SUPPORT_ENABLED"] !== "false",
    ANALYTICS: process.env["REACT_APP_ANALYTICS_ENABLED"] !== "false",
    NOTIFICATIONS: process.env["REACT_APP_NOTIFICATIONS_ENABLED"] !== "false",
    REALTIME: process.env["REACT_APP_REALTIME_ENABLED"] !== "false",
    WEBSOCKET: process.env["REACT_APP_WEBSOCKET_ENABLED"] !== "false",
  },

  // Performance
  PERFORMANCE: {
    LAZY_LOADING: process.env["REACT_APP_LAZY_LOADING"] !== "false",
    CODE_SPLITTING: process.env["REACT_APP_CODE_SPLITTING"] !== "false",
    VIRTUAL_SCROLLING: process.env["REACT_APP_VIRTUAL_SCROLLING"] !== "false",
    CACHE_ENABLED: process.env["REACT_APP_CACHE_ENABLED"] !== "false",
    CACHE_TTL: parseInt(process.env["REACT_APP_CACHE_TTL"] || "300000"),
  },

  // File Upload
  FILE_UPLOAD: {
    MAX_SIZE: parseInt(process.env["REACT_APP_MAX_FILE_SIZE"] || "10485760"), // 10MB
    ALLOWED_TYPES:
      process.env["REACT_APP_ALLOWED_FILE_TYPES"] ||
      "jpg,jpeg,png,pdf,doc,docx,xls,xlsx",
    CDN_URL: process.env["REACT_APP_CDN_URL"] || "",
  },

  // Internationalization
  I18N: {
    // D-A2 (05.08.2026): канонический язык по умолчанию — английский; русский
    // остаётся одним из поддерживаемых, но не основным. Раньше здесь стоял "ru",
    // и это расходилось с соседним `i18n/config.ts` (`fallbackLng = "en"`, целевой
    // рынок EU/US) — дефолт был объявлен независимо в двух местах фронта и семи
    // местах бэкенда.
    DEFAULT_LANGUAGE: process.env["REACT_APP_DEFAULT_LANGUAGE"] || "en",
    SUPPORTED_LANGUAGES: process.env["REACT_APP_SUPPORTED_LANGUAGES"]?.split(
      ",",
    ) || ["en", "de", "fr", "es", "ru"],
    RTL_SUPPORT: process.env["REACT_APP_RTL_SUPPORT"] === "true",
  },

  // Support / Help — внешние ссылки (опциональны; пусто → внутренний help-диалог)
  SUPPORT: {
    FAQ_URL: process.env["REACT_APP_FAQ_URL"] || "",
    KNOWLEDGE_BASE_URL: process.env["REACT_APP_KNOWLEDGE_BASE_URL"] || "",
  },

  // Logging & Monitoring
  LOGGING: {
    LEVEL: process.env["REACT_APP_LOG_LEVEL"] || "info",
    SENTRY_DSN: process.env["REACT_APP_SENTRY_DSN"] || "",
    ERROR_REPORTING: process.env["REACT_APP_ERROR_REPORTING"] !== "false",
    PERFORMANCE_MONITORING:
      process.env["REACT_APP_PERFORMANCE_MONITORING"] !== "false",
  },

  // Third-party APIs
  THIRD_PARTY: {
    GOOGLE_MAPS_JS_KEY:
      process.env["REACT_APP_GOOGLE_MAPS_JS_KEY"] ||
      process.env["REACT_APP_GOOGLE_MAPS_API_KEY"] ||
      "",
    GOOGLE_MAPS_API_KEY: process.env["REACT_APP_GOOGLE_MAPS_API_KEY"] || "",
    STRIPE_PUBLIC_KEY: process.env["REACT_APP_STRIPE_PUBLIC_KEY"] || "",
    // Зарезервировано для соц-логина (Google/Facebook) — задекларирован в Политике
    // конфиденциальности §4 и на /sub-processors, но UI/BE-верификация токена ещё не
    // реализованы. Отложенная трекнутая фича (token-verification SPA): НЕ удалять как
    // «мёртвый конфиг». См. compliance-стрим (R8) / memory.
    GOOGLE_CLIENT_ID: process.env["REACT_APP_GOOGLE_CLIENT_ID"] || "",
    FACEBOOK_APP_ID: process.env["REACT_APP_FACEBOOK_APP_ID"] || "",
  },

  // Application Info
  APP: {
    VERSION: process.env["REACT_APP_VERSION"] || "1.5.0",
    BUILD_NUMBER: process.env["REACT_APP_BUILD_NUMBER"] || "development",
    BUILD_DATE: process.env["REACT_APP_BUILD_DATE"] || new Date().toISOString(),
    GIT_COMMIT: process.env["REACT_APP_GIT_COMMIT_HASH"] || "dev-commit",
  },

  // Environment detection
  IS_PRODUCTION: process.env["NODE_ENV"] === "production",
  IS_DEVELOPMENT: process.env["NODE_ENV"] === "development",
  IS_TEST: process.env["NODE_ENV"] === "test",
};

// ======= ENVIRONMENT VALIDATION =======

export const validateEnvironment = (): void => {
  const requiredVars = ["API_BASE_URL", "BACKEND_URL"];
  const missing = requiredVars.filter(
    (_key) => !config.API_BASE_URL || !config.BACKEND_URL,
  );

  if (missing.length > 0) {
    console.warn("⚠️ Missing required environment variables:", missing);
  }

  if (config.IS_PRODUCTION && config.USE_MOCK_DATA) {
    console.error("❌ CRITICAL: Mock data is enabled in production!");
  }

  if (config.IS_PRODUCTION && config.DEBUG_MODE) {
    console.warn("⚠️ Debug mode is enabled in production");
  }
};

// ======= LOGGING HELPER =======

export const logEnvironmentInfo = (): void => {
  if (process.env["NODE_ENV"] === "development") {
    /* eslint-disable no-console */
    console.log("🚀 Event Master v1.5 Environment Configuration:");
    console.log(
      "📋 Mode:",
      config.IS_PRODUCTION ? "Production" : "Development",
    );
    console.log("🔗 Backend URL:", config.BACKEND_URL);
    console.log("⚙️ Enterprise Mode:", config.ENTERPRISE_MODE);
    console.log(
      "🎭 Mock Data:",
      config.USE_MOCK_DATA ? "❌ Enabled" : "✅ Disabled",
    );
    console.log(
      "🔧 Debug Mode:",
      config.DEBUG_MODE ? "❌ Enabled" : "✅ Disabled",
    );
    console.log("📦 Version:", config.APP.VERSION);
    console.log("🏗️ Build:", config.APP.BUILD_NUMBER);
    /* eslint-enable no-console */
  }
};

// Auto-validate on import
validateEnvironment();

export default config;
